import { cn } from '@/lib/utils';
import React from 'react';

export interface PageTitleProps {
  /**
   * The main title of the page
   */
  title: string;
  /**
   * Optional subtitle or description text
   */
  subtitle?: string;
  /**
   * Optional content to display on the right side (buttons, controls, etc.)
   */
  actions?: React.ReactNode;
  /**
   * Optional CSS class name for the container
   */
  className?: string;
}

/**
 * PageTitle component provides a consistent layout for page headers
 *
 * Features:
 * - Standardized typography with h1 title and muted subtitle
 * - Flexible right-side actions area for buttons or controls
 * - Responsive design that stacks on smaller screens
 * - Proper accessibility with semantic HTML structure
 * - Consistent spacing and styling across all pages
 *
 * @example
 * ```tsx
 * <PageTitle
 *   title="Users Management"
 *   subtitle="Manage and view system users (25 total)"
 *   actions={
 *     <Button onClick={handleRefresh}>
 *       <RefreshCw className="h-4 w-4" />
 *       Refresh
 *     </Button>
 *   }
 * />
 * ```
 */
export function PageTitle({
  title,
  subtitle,
  actions,
  className = '',
}: PageTitleProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-4 justify-between md:flex-row md:items-center',
        className
      )}
    >
      <div className='space-y-1'>
        <h1 className='text-xl font-semibold tracking-tight sm:text-2xl lg:text-3xl'>
          {title}
        </h1>
        {subtitle && (
          <p className='text-sm text-muted-foreground sm:text-base'>
            {subtitle}
          </p>
        )}
      </div>
      {actions && (
        <div className='flex flex-wrap justify-end gap-2 md:flex-nowrap'>
          {actions}
        </div>
      )}
    </div>
  );
}

export default PageTitle;
